import React, { useState, useEffect } from 'react';
import { 
  Sparkles, Calendar, History, ClipboardList, FileText,
  GraduationCap, Layers, LayoutList, Send, CheckCircle, XCircle, Clock, Eye, ChevronDown, ChevronUp
} from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { getCurrentUser } from '@/data/users';
import { useLanguage } from '@/contexts/LanguageContext';
import BatchGeneratorTab from '@/components/batch/BatchGeneratorTab';
import { GenerateLessonTab } from '@/components/ai-planner/GenerateLessonTab';
import { WeekPlanTab } from '@/components/ai-planner/WeekPlanTab';
import { SchemeTab } from '@/components/ai-planner/SchemeTab';
import { NotesTab } from '@/components/ai-planner/NotesTab';
import { ExamTab } from '@/components/ai-planner/ExamTab';
import { AssessmentTab } from '@/components/ai-planner/AssessmentTab';
import { HistoryTab } from '@/components/ai-planner/HistoryTab';

const API_URL = '';
const authHeader = () => { const t = localStorage.getItem('authToken'); return t ? { Authorization: `Bearer ${t}` } : {}; };

// Component to view approved submission content (returned to teacher)
function SubmissionContentViewer({ submissionId, type }) {
  const [expanded, setExpanded] = useState(false);
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadContent = async () => {
    if (content) { setExpanded(!expanded); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/academic/submission/${submissionId}`);
      if (res.ok) {
        const data = await res.json();
        setContent(typeof data.content === 'string' ? JSON.parse(data.content) : data.content);
        setExpanded(true);
      }
    } catch (e) { console.error('Failed to load content:', e); }
    finally { setLoading(false); }
  };

  return (
    <div className="mt-3">
      <Button variant="outline" size="sm" onClick={loadContent} disabled={loading}
        className="text-green-700 border-green-300 hover:bg-green-50"
        data-testid={`view-approved-${submissionId}`}>
        <Eye className="h-3 w-3 mr-1" />
        {loading ? 'Loading...' : expanded ? 'Hide Approved Plan' : 'View Approved Plan'}
        {expanded ? <ChevronUp className="h-3 w-3 ml-1" /> : <ChevronDown className="h-3 w-3 ml-1" />}
      </Button>
      {expanded && content && (
        <div className="mt-3 p-4 bg-green-50 border border-green-200 rounded-lg max-h-96 overflow-y-auto">
          <p className="text-xs text-green-700 font-semibold mb-3">Approved</p>
          {type === 'scheme' && Array.isArray(content) ? (
            <div className="space-y-2">
              {content.map((week, idx) => (
                <div key={idx} className="p-2 bg-white rounded border text-sm">
                  <strong>Week {week.week || idx + 1}:</strong> {week.topic || week.title || 'No topic'}
                  {week.objectives && <p className="text-xs text-gray-500 mt-1">Objectives: {Array.isArray(week.objectives) ? week.objectives.join(', ') : week.objectives}</p>}
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-2 text-sm">
              {content.title && <h5 className="font-semibold">{content.title}</h5>}
              {content.objectives && (
                <div><p className="font-medium">Objectives:</p>
                  <ul className="list-disc list-inside text-gray-600">
                    {(Array.isArray(content.objectives) ? content.objectives : [content.objectives]).map((o, i) => <li key={i}>{o}</li>)}
                  </ul>
                </div>
              )}
              {content.activities && (
                <div><p className="font-medium">Activities:</p>
                  <p className="text-gray-600">{Array.isArray(content.activities) ? content.activities.join(', ') : content.activities}</p>
                </div>
              )}
              {content.plans && Array.isArray(content.plans) && (
                <div className="space-y-2">
                  {content.plans.map((day, idx) => (
                    <div key={idx} className="p-2 bg-white rounded border">
                      <strong>{day.day || `Day ${idx+1}`}:</strong> {day.title || 'Lesson'}
                      {day.objectives && <p className="text-xs text-gray-500 mt-1">{Array.isArray(day.objectives) ? day.objectives.join(', ') : day.objectives}</p>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Resilient fetch with retry and timeout for Neon DB cold starts
async function resilientFetch(url, options = {}, maxRetries = 3) {
  const isGeneration = url.includes('/generate');
  const timeoutMs = isGeneration ? 90000 : 25000;
  const retries = isGeneration ? 1 : maxRetries;

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
      const response = await fetch(url, { ...options, signal: controller.signal });
      clearTimeout(timeoutId);
      if (response.status >= 500 && attempt < retries) {
        await new Promise(r => setTimeout(r, 2000 * (attempt + 1)));
        continue;
      }
      return response;
    } catch (error) {
      if (attempt < retries && (error.name === 'AbortError' || error.message === 'Failed to fetch')) {
        await new Promise(r => setTimeout(r, 2000 * (attempt + 1)));
        continue;
      }
      throw error;
    }
  }
}

export default function AILessonPlannerPage() {
  const currentUser = getCurrentUser();
  const { isFrench, t } = useLanguage();
  
  // Read tab from URL query params (e.g., ?tab=scheme)
  const urlParams = new URLSearchParams(window.location.search);
  const initialTab = urlParams.get('tab') || 'generate';
  
  // Determine school type and sensible defaults
  const dept = currentUser?.department?.toLowerCase() || '';
  const isPrimarySchool = currentUser?.school_type === 'primary';
  const defaultSubject = isPrimarySchool ? 'english' : 'ict';
  const defaultYear = isPrimarySchool ? (parseInt(dept.match(/\d+/)?.[0]) || 1) : 9;
  
  const [activeTab, setActiveTab] = useState(initialTab);
  const [loading, setLoading] = useState(false);
  const [generatedLesson, setGeneratedLesson] = useState(null);
  const [generatedWeek, setGeneratedWeek] = useState(null);
  const [generatedAssessment, setGeneratedAssessment] = useState(null);
  const [savedLessons, setSavedLessons] = useState([]);
  const [savedAssessments, setSavedAssessments] = useState([]);
  const [loadingSaved, setLoadingSaved] = useState(false);
  const [teacherSubjects, setTeacherSubjects] = useState(null);
  const [teacherYearLevels, setTeacherYearLevels] = useState(null);
  const [generatedScheme, setGeneratedScheme] = useState(null);
  const [schemeLoading, setSchemeLoading] = useState(false);
  const [generatedExam, setGeneratedExam] = useState(null);
  const [examLoading, setExamLoading] = useState(false);
  const [sharedContentIds, setSharedContentIds] = useState({});
  const [allTags, setAllTags] = useState([]);
  const [selectedTag, setSelectedTag] = useState('');
  const [editingTags, setEditingTags] = useState(null);
  const [newTagInput, setNewTagInput] = useState('');
  const [viewingSubmissions, setViewingSubmissions] = useState(null);
  const [submissionsData, setSubmissionsData] = useState(null);
  const [loadingSubmissions, setLoadingSubmissions] = useState(false);
  const [sharingToAcademic, setSharingToAcademic] = useState(false);
  const [teacherSubmissions, setTeacherSubmissions] = useState([]);

  // Determine if this is a primary school - submissions go to Principal instead of Academic
  const shareLabel = isPrimarySchool ? 'Submit to Principal' : 'Share to Academic';
  const submissionTarget = isPrimarySchool ? 'Principal' : 'Academic Principal';

  const [examForm, setExamForm] = useState({
    subject: defaultSubject,
    year_level: defaultYear,
    term: 1,
    school_type: isPrimarySchool ? 'primary' : 'secondary',
    duration_minutes: 120,
    total_marks: 100,
    topics_covered: '',
    language: isFrench ? 'fr' : 'en'
  });
  const [schemeForm, setSchemeForm] = useState({
    subject: defaultSubject,
    year_level: defaultYear,
    term: 1,
    num_weeks: 10,
    school_type: isPrimarySchool ? 'primary' : 'secondary',
    language: isFrench ? 'fr' : 'en',
    strand: ''
  });
  const [lessonForm, setLessonForm] = useState({
    topic: '',
    subject: defaultSubject,
    year_level: defaultYear,
    duration: 45,
    school_type: isPrimarySchool ? 'primary' : 'secondary',
    language: isFrench ? 'fr' : 'en',
    student_abilities: 'mixed',
    teaching_style: 'interactive'
  });
  const [assessmentForm, setAssessmentForm] = useState({
    topic: '',
    subject: defaultSubject,
    year_level: defaultYear,
    assessment_type: 'quiz',
    difficulty: 'medium',
    num_questions: 10,
    question_types: ['multiple_choice', 'true_false'],
    school_type: isPrimarySchool ? 'primary' : 'secondary',
    language: isFrench ? 'fr' : 'en',
    include_answer_key: true
  });

  // ========== Helper functions ==========
  const getYearOptions = () => {
    if (teacherYearLevels && teacherYearLevels.length > 0) return teacherYearLevels;
    if (isPrimarySchool) return [1, 2, 3, 4, 5, 6];
    return [7, 8, 9, 10, 11, 12, 13];
  };

  const getSubjectOptions = () => {
    const subjectMap = {
      'General': { value: 'general', label: 'General' },
      'English': { value: 'english', label: 'English' },
      'Mathematics': { value: 'mathematics', label: 'Mathematics' },
      'Science': { value: 'science', label: 'Science' },
      'Social Science': { value: 'social_science', label: 'Social Science' },
      'Arts & Craft': { value: 'arts_craft', label: 'Arts & Craft' },
      'Physical Education': { value: 'physical_education', label: 'Physical Education' },
      'Religious Education': { value: 'religious_education', label: 'Religious Education' },
      'Vernacular': { value: 'vernacular', label: 'Vernacular' },
      'ICT': { value: 'ict', label: 'ICT / Computer Studies' },
      'ICT / Computer Studies': { value: 'ict', label: 'ICT / Computer Studies' },
      'Computer Studies': { value: 'ict', label: 'ICT / Computer Studies' },
      'Home Economics': { value: 'home_economics', label: 'Home Economics' },
      'Food & Nutrition': { value: 'food_nutrition', label: 'Food & Nutrition' },
      'Food and Nutrition': { value: 'food_nutrition', label: 'Food & Nutrition' },
      'Textile & Garment': { value: 'textile_garment', label: 'Textile & Garment' },
      'Chemistry': { value: 'chemistry', label: 'Chemistry' },
      'Physics': { value: 'physics', label: 'Physics' },
      'Biology': { value: 'biology', label: 'Biology' },
      'Agriculture': { value: 'agriculture', label: 'Agriculture' },
      'Geography': { value: 'geography', label: 'Geography' },
      'History': { value: 'history', label: 'History' },
      'Economics': { value: 'economics', label: 'Economics' },
      'French': { value: 'french', label: 'French' },
      'Primary Education': { value: 'general', label: 'General / Primary Education' },
      'Language': { value: 'english', label: 'Language & Communication' },
    };

    if (teacherSubjects && teacherSubjects.length > 0) {
      const seen = new Set();
      const options = [];
      for (const subj of teacherSubjects) {
        const mapped = subjectMap[subj];
        if (mapped && !seen.has(mapped.value)) {
          seen.add(mapped.value);
          options.push(mapped);
        }
      }
      if (options.length <= 1) {
        const isPrimary = currentUser?.school_type === 'primary';
        if (isPrimary) {
          return [
            { value: 'english', label: 'English / Language' },
            { value: 'mathematics', label: 'Mathematics' },
            { value: 'science', label: 'Science' },
            { value: 'social_science', label: 'Social Science' },
            { value: 'arts_craft', label: 'Arts & Craft' },
            { value: 'physical_education', label: 'Physical Education' },
          ];
        }
      }
      return options;
    }

    const isPrimary = currentUser?.school_type === 'primary';
    if (isPrimary) {
      return [
        { value: 'english', label: 'English / Language' },
        { value: 'mathematics', label: 'Mathematics' },
        { value: 'science', label: 'Science' },
        { value: 'social_science', label: 'Social Science' },
        { value: 'arts_craft', label: 'Arts & Craft' },
        { value: 'physical_education', label: 'Physical Education' },
        { value: 'vernacular', label: 'Vernacular' },
        { value: 'religious_education', label: 'Religious Education' },
      ];
    }

    return [
      { value: 'ict', label: 'ICT / Computer Studies' },
      { value: 'mathematics', label: 'Mathematics' },
      { value: 'science', label: 'Science' },
      { value: 'english', label: 'English' },
      { value: 'chemistry', label: 'Chemistry' },
      { value: 'physics', label: 'Physics' },
      { value: 'biology', label: 'Biology' },
      { value: 'agriculture', label: 'Agriculture' },
      { value: 'geography', label: 'Geography' },
      { value: 'history', label: 'History' },
      { value: 'economics', label: 'Economics' },
      { value: 'french', label: 'French' },
      { value: 'home_economics', label: 'Home Economics' },
      { value: 'food_nutrition', label: 'Food & Nutrition' },
      { value: 'textile_garment', label: 'Textile & Garment' },
    ];
  };

  // ========== Effects ==========
  useEffect(() => {
    const fetchTeacherData = async () => {
      if (!currentUser?.id) return;
      const token = localStorage.getItem('authToken');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      try {
        const subRes = await resilientFetch(`${API_URL}/api/classes/subjects?teacher_id=${currentUser.id}`, { headers });
        if (subRes.ok) {
          const subjects = await subRes.json();
          setTeacherSubjects(subjects);
          localStorage.setItem('teacherSubjects', JSON.stringify(subjects));
        }
        const clsRes = await resilientFetch(`${API_URL}/api/classes/?teacher_id=${currentUser.id}`, { headers });
        if (clsRes.ok) {
          const classes = await clsRes.json();
          const years = [...new Set(classes.map(c => c.year).filter(Boolean))].sort((a, b) => a - b);
          if (years.length > 0) {
            setTeacherYearLevels(years);
            localStorage.setItem('teacherYearLevels', JSON.stringify(years));
          }
        }
      } catch (err) {
        console.error('Error fetching teacher data:', err);
        const cachedSubjects = localStorage.getItem('teacherSubjects');
        const cachedYears = localStorage.getItem('teacherYearLevels');
        if (cachedSubjects) setTeacherSubjects(JSON.parse(cachedSubjects));
        if (cachedYears) setTeacherYearLevels(JSON.parse(cachedYears));
      }
    };
    fetchTeacherData();
  }, [currentUser?.id]);

  useEffect(() => {
    if (teacherSubjects && teacherSubjects.length > 0) {
      const opts = getSubjectOptions();
      if (opts.length > 0) {
        const firstSubject = opts[0].value;
        setLessonForm(prev => ({ ...prev, subject: firstSubject }));
        setAssessmentForm(prev => ({ ...prev, subject: firstSubject }));
        setSchemeForm(prev => ({ ...prev, subject: firstSubject }));
        setExamForm(prev => ({ ...prev, subject: firstSubject }));
      }
    }
    if (teacherYearLevels && teacherYearLevels.length > 0) {
      const firstYear = teacherYearLevels[0];
      setLessonForm(prev => ({ ...prev, year_level: firstYear }));
      setAssessmentForm(prev => ({ ...prev, year_level: firstYear }));
      setSchemeForm(prev => ({ ...prev, year_level: firstYear }));
      setExamForm(prev => ({ ...prev, year_level: firstYear }));
    }
  }, [teacherSubjects, teacherYearLevels]);

  useEffect(() => {
    if (activeTab === 'history') {
      loadSavedLessons();
      loadSavedAssessments();
      loadSharedContent();
      loadAllTags();
    }
    if (activeTab === 'submissions') {
      loadTeacherSubmissions();
    }
  }, [activeTab]);

  // ========== Data loaders ==========
  const loadTeacherSubmissions = async () => {
    if (!currentUser?.id) return;
    setLoadingSubmissions(true);
    try {
      const res = await resilientFetch(`${API_URL}/api/academic/teacher-submissions/${currentUser.id}`);
      if (res.ok) {
        const data = await res.json();
        setTeacherSubmissions(data);
      }
    } catch (error) {
      console.error('Error loading submissions:', error);
    } finally {
      setLoadingSubmissions(false);
    }
  };

  const loadSavedLessons = async () => {
    if (!currentUser?.id) return;
    setLoadingSaved(true);
    try {
      const response = await resilientFetch(`${API_URL}/api/ai-lessons/my-lessons?teacher_id=${currentUser.id}`);
      if (response.ok) setSavedLessons(await response.json());
    } catch (error) {
      console.error('Error loading saved lessons:', error);
    } finally {
      setLoadingSaved(false);
    }
  };

  const loadSavedAssessments = async () => {
    if (!currentUser?.id) return;
    try {
      const response = await resilientFetch(`${API_URL}/api/ai-lessons/my-assessments?teacher_id=${currentUser.id}`);
      if (response.ok) setSavedAssessments(await response.json());
    } catch (error) {
      console.error('Error loading saved assessments:', error);
    }
  };

  const loadSharedContent = async () => {
    if (!currentUser?.id) return;
    try {
      const response = await resilientFetch(`${API_URL}/api/ai-lessons/shared-content?teacher_id=${currentUser.id}`, { headers: authHeader() });
      if (response.ok) {
        const data = await response.json();
        const map = {};
        data.forEach(s => {
          if (s.lesson_id) map[s.lesson_id] = s.id;
          if (s.assessment_id) map[s.assessment_id] = s.id;
        });
        setSharedContentIds(map);
      }
    } catch (error) {
      console.error('Error loading shared content:', error);
    }
  };

  const loadAllTags = async () => {
    if (!currentUser?.id) return;
    try {
      const response = await resilientFetch(`${API_URL}/api/ai-lessons/all-tags?teacher_id=${currentUser.id}`);
      if (response.ok) {
        const data = await response.json();
        setAllTags(data.tags || []);
      }
    } catch (error) {
      console.error('Error loading tags:', error);
    }
  };

  // ========== CRUD & sharing handlers ==========
  const updateItemTags = async (id, type, tags) => {
    const endpoint = type === 'lesson' ? 'lesson' : 'assessment';
    try {
      const response = await resilientFetch(`${API_URL}/api/ai-lessons/${endpoint}/${id}/tags`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tags })
      });
      if (response.ok) {
        toast.success('Tags updated');
        if (type === 'lesson') setSavedLessons(prev => prev.map(l => l.id === id ? { ...l, tags } : l));
        else setSavedAssessments(prev => prev.map(a => a.id === id ? { ...a, tags } : a));
        loadAllTags();
      }
    } catch (error) {
      toast.error('Failed to update tags');
    }
  };

  const shareNote = async (lessonId) => {
    try {
      const response = await resilientFetch(`${API_URL}/api/ai-lessons/share-note/${lessonId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ teacher_id: currentUser.id, school_id: currentUser.school_id })
      });
      const data = await response.json();
      if (response.ok) {
        toast.success('Notes shared with students!');
        setSharedContentIds(prev => ({ ...prev, [lessonId]: data.shared_id }));
      } else {
        toast.error(data.detail || 'Failed to share');
      }
    } catch (error) {
      toast.error('Server is slow. Please try again.');
    }
  };

  // Exam mode settings state for share dialog
  const [shareDialog, setShareDialog] = useState(null); // assessment to share
  const [shareLockdown, setShareLockdown] = useState(false);
  const [shareMaxTabSwitches, setShareMaxTabSwitches] = useState(2);

  const openShareDialog = (assessmentId) => {
    setShareDialog(assessmentId);
    // Auto-detect: tests/quizzes/exams default to lockdown ON, worksheets/practice default OFF
    const assessment = savedAssessments.find(a => a.id === assessmentId);
    const type = (assessment?.assessment_type || '').toLowerCase();
    const isTest = ['test', 'quiz', 'exam', 'termly_exam'].includes(type);
    setShareLockdown(isTest);
    setShareMaxTabSwitches(2);
  };

  const confirmShareAssessment = async () => {
    if (!shareDialog) return;
    try {
      const response = await resilientFetch(`${API_URL}/api/ai-lessons/share-assessment/${shareDialog}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          teacher_id: currentUser.id,
          school_id: currentUser.school_id,
          lockdown_mode: shareLockdown,
          max_tab_switches: shareMaxTabSwitches
        })
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(`Assessment shared${shareLockdown ? ' with exam lockdown' : ''}!`);
        setSharedContentIds(prev => ({ ...prev, [shareDialog]: data.shared_id }));
      } else {
        toast.error(data.detail || 'Failed to share');
      }
    } catch (error) {
      toast.error('Server is slow. Please try again.');
    }
    setShareDialog(null);
  };

  const shareAssessment = async (assessmentId) => {
    openShareDialog(assessmentId);
  };

  const unshareContent = async (contentId, sharedId, force = false) => {
    try {
      const url = `${API_URL}/api/ai-lessons/unshare/${sharedId}?teacher_id=${currentUser.id}${force ? '&force=true' : ''}`;
      const response = await resilientFetch(url, { method: 'DELETE', headers: authHeader() });
      if (response.ok) {
        toast.success('Content unshared');
        setSharedContentIds(prev => { const u = { ...prev }; delete u[contentId]; return u; });
      } else if (response.status === 409) {
        const data = await response.json();
        if (window.confirm(`WARNING: Students have already submitted work!\n\n${data.detail}\n\nAre you SURE?`)) {
          unshareContent(contentId, sharedId, true);
        }
      }
    } catch (error) {
      toast.error('Failed to unshare. Please try again.');
    }
  };

  const viewSubmissions = async (sharedId) => {
    setLoadingSubmissions(true);
    setViewingSubmissions(sharedId);
    try {
      const response = await resilientFetch(`${API_URL}/api/ai-lessons/submissions/${sharedId}?teacher_id=${currentUser.id}`, { headers: authHeader() });
      if (response.ok) setSubmissionsData(await response.json());
    } catch (error) {
      toast.error('Failed to load submissions');
    } finally {
      setLoadingSubmissions(false);
    }
  };

  const gradeSubmission = async (submissionId, marks, feedback) => {
    try {
      const response = await resilientFetch(`${API_URL}/api/ai-lessons/grade-submission/${submissionId}?teacher_id=${currentUser.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ marks: parseInt(marks), feedback })
      });
      if (response.ok) {
        toast.success('Submission graded!');
        if (viewingSubmissions) viewSubmissions(viewingSubmissions);
      } else {
        toast.error('Failed to grade');
      }
    } catch (error) {
      toast.error('Failed to grade submission');
    }
  };

  // ========== Generation handlers ==========
  const generateLesson = async () => {
    if (!lessonForm.topic.trim()) { toast.error('Please enter a topic for the lesson'); return; }
    setLoading(true);
    setGeneratedLesson(null);
    try {
      const response = await resilientFetch(`${API_URL}/api/ai-lessons/generate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(lessonForm)
      });
      if (!response.ok) throw new Error('Failed to generate lesson');
      const data = await response.json();
      setGeneratedLesson(data.lesson);
      toast.success('Lesson plan generated successfully!');
    } catch (error) {
      console.error('Error generating lesson:', error);
      toast.error('Failed to generate lesson. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const generateWeekPlan = async () => {
    if (!lessonForm.topic.trim()) { toast.error('Please enter a topic for the week'); return; }
    setLoading(true);
    setGeneratedWeek(null);
    try {
      const response = await resilientFetch(`${API_URL}/api/ai-lessons/generate-week`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(lessonForm)
      });
      if (!response.ok) throw new Error('Failed to generate week plan');
      const data = await response.json();
      setGeneratedWeek(data.lessons);
      toast.success('Week plan generated successfully!');
    } catch (error) {
      console.error('Error generating week plan:', error);
      toast.error('Failed to generate week plan. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const generateAssessment = async () => {
    if (!assessmentForm.topic.trim()) { toast.error('Please enter a topic for the assessment'); return; }
    setLoading(true);
    setGeneratedAssessment(null);
    try {
      // Warm up DB connection first
      fetch(`${API_URL}/api/warmup`).catch(() => {});
      const response = await resilientFetch(`${API_URL}/api/ai-lessons/generate-assessment`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(assessmentForm)
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.detail || `Server error (${response.status})`);
      }
      const data = await response.json();
      setGeneratedAssessment(data.assessment);
      toast.success('Assessment generated successfully!');
    } catch (error) {
      console.error('Error generating assessment:', error);
      toast.error(error.message?.includes('AbortError') || error.name === 'AbortError'
        ? 'Generation timed out. Try fewer questions or try again.'
        : `Failed to generate assessment: ${error.message || 'Please try again.'}`);
    } finally {
      setLoading(false);
    }
  };

  const generateScheme = async () => {
    setSchemeLoading(true);
    setGeneratedScheme(null);
    try {
      const response = await resilientFetch(`${API_URL}/api/ai-lessons/generate-scheme`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...schemeForm, strand: schemeForm.strand || null })
      });
      if (!response.ok) throw new Error('Failed to generate scheme');
      const data = await response.json();
      setGeneratedScheme(data.weeks);
      toast.success('Scheme of Work generated!');
    } catch (error) {
      console.error('Error generating scheme:', error);
      toast.error('Failed to generate scheme. Please try again.');
    } finally {
      setSchemeLoading(false);
    }
  };

  const generateTermlyExam = async () => {
    if (!examForm.topics_covered) { toast.error('Please enter the topics covered'); return; }
    setExamLoading(true);
    setGeneratedExam(null);
    try {
      const response = await resilientFetch(`${API_URL}/api/ai-lessons/generate-exam`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subject: examForm.subject, year_level: examForm.year_level, term: examForm.term,
          school_type: examForm.school_type, duration_minutes: examForm.duration_minutes,
          total_marks: examForm.total_marks, topics_covered: examForm.topics_covered, language: examForm.language
        })
      });
      if (!response.ok) throw new Error('Failed to generate exam');
      const data = await response.json();
      setGeneratedExam(data);
      toast.success('Termly exam generated!');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to generate exam. Please try again.');
    } finally {
      setExamLoading(false);
    }
  };

  // ========== Save handlers ==========
  const saveLesson = async (lesson, isWeekPlan = false, weekLessons = null) => {
    if (!currentUser?.id) { toast.error('Please log in to save lessons'); return; }
    try {
      const payload = {
        title: lesson.title, subject: lessonForm.subject, year_level: lessonForm.year_level,
        duration: lessonForm.duration, topic: lessonForm.topic, objectives: lesson.objectives,
        introduction: lesson.introduction, activities: lesson.activities, materials: lesson.materials,
        assessment: lesson.assessment, homework: lesson.homework, is_week_plan: isWeekPlan,
        week_lessons: weekLessons
      };
      const response = await resilientFetch(`${API_URL}/api/ai-lessons/save-lesson?teacher_id=${currentUser.id}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload)
      });
      if (!response.ok) throw new Error('Failed to save lesson');
      toast.success('Lesson saved to your library!');
      loadSavedLessons();
    } catch (error) {
      console.error('Error saving lesson:', error);
      toast.error('Failed to save lesson');
    }
  };

  const saveAssessment = async (assessment) => {
    if (!currentUser?.id) { toast.error('Please log in to save assessments'); return; }
    try {
      const payload = {
        title: assessment.title, subject: assessmentForm.subject, year_level: assessmentForm.year_level,
        topic: assessmentForm.topic, assessment_type: assessmentForm.assessment_type,
        difficulty: assessmentForm.difficulty, questions: assessment.questions,
        answer_key: assessment.answer_key, total_marks: assessment.total_marks,
        time_limit: assessment.time_limit, instructions: assessment.instructions
      };
      const response = await resilientFetch(`${API_URL}/api/ai-lessons/save-assessment?teacher_id=${currentUser.id}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload)
      });
      if (!response.ok) throw new Error('Failed to save assessment');
      toast.success('Assessment saved to your library!');
      loadSavedAssessments();
    } catch (error) {
      console.error('Error saving assessment:', error);
      toast.error('Failed to save assessment');
    }
  };

  const saveScheme = async () => {
    if (!currentUser?.id || !generatedScheme) return;
    try {
      const subjectLabel = getSubjectOptions().find(s => s.value === schemeForm.subject)?.label || schemeForm.subject;
      const payload = {
        title: `Scheme of Work: Year ${schemeForm.year_level} ${subjectLabel} - Term ${schemeForm.term}`,
        subject: schemeForm.subject, year_level: schemeForm.year_level, term: String(schemeForm.term),
        topic: `Term ${schemeForm.term} - ${schemeForm.num_weeks} weeks`, is_week_plan: true,
        week_lessons: generatedScheme
      };
      let response;
      for (let attempt = 0; attempt < 3; attempt++) {
        response = await resilientFetch(`${API_URL}/api/ai-lessons/save-lesson?teacher_id=${currentUser.id}`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload)
        });
        if (response.ok || response.status < 500) break;
        await new Promise(r => setTimeout(r, 2000));
      }
      if (!response.ok) throw new Error('Failed to save scheme');
      toast.success('Scheme of Work saved to your library!');
      loadSavedLessons();
    } catch (error) {
      console.error('Error saving scheme:', error);
      toast.error('Server busy - please wait a moment and try saving again');
    }
  };

  // Share Scheme of Work to Academic Principal for approval
  const shareSchemeToAcademic = async () => {
    if (!currentUser?.id || !generatedScheme) return;
    if (!currentUser.school_id) {
      toast.error('School ID not found. Please log out and log in again.');
      return;
    }
    setSharingToAcademic(true);
    try {
      const subjectLabel = getSubjectOptions().find(s => s.value === schemeForm.subject)?.label || schemeForm.subject;
      const payload = {
        teacher_id: currentUser.id,
        teacher_name: currentUser.name,
        school_id: currentUser.school_id,
        submission_type: 'scheme',
        title: `Scheme of Work: Year ${schemeForm.year_level} ${subjectLabel} - Term ${schemeForm.term}`,
        subject: subjectLabel,
        year_level: schemeForm.year_level,
        term: schemeForm.term,
        content: generatedScheme
      };
      const response = await resilientFetch(`${API_URL}/api/academic/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to submit');
      toast.success(`Scheme submitted to ${submissionTarget} for approval!`);
    } catch (error) {
      console.error('Error sharing to academic:', error);
      toast.error(`Failed to submit to ${submissionTarget}. Please try again.`);
    } finally {
      setSharingToAcademic(false);
    }
  };

  // Share Lesson Plan to Academic Principal for approval
  const shareLessonToAcademic = async (lesson) => {
    if (!currentUser?.id || !lesson) return;
    if (!currentUser.school_id) {
      toast.error('School ID not found. Please log out and log in again.');
      return;
    }
    setSharingToAcademic(true);
    try {
      const subjectLabel = getSubjectOptions().find(s => s.value === lessonForm.subject)?.label || lessonForm.subject;
      const payload = {
        teacher_id: currentUser.id,
        teacher_name: currentUser.name,
        school_id: currentUser.school_id,
        submission_type: 'lesson_plan',
        title: lesson.title || `Lesson Plan: Year ${lessonForm.year_level} ${subjectLabel} - ${lessonForm.topic}`,
        subject: subjectLabel,
        year_level: lessonForm.year_level,
        term: 1,
        content: lesson
      };
      const response = await resilientFetch(`${API_URL}/api/academic/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to submit');
      toast.success(`Lesson Plan submitted to ${submissionTarget} for approval!`);
    } catch (error) {
      console.error('Error sharing to academic:', error);
      toast.error(`Failed to submit to ${submissionTarget}. Please try again.`);
    } finally {
      setSharingToAcademic(false);
    }
  };

  const saveTermlyExam = async () => {
    if (!generatedExam || !currentUser?.id) return;
    try {
      const subjectLabel = getSubjectOptions().find(s => s.value === examForm.subject)?.label || examForm.subject;
      const allQuestions = (generatedExam.sections || []).flatMap(s => s.questions || []);
      const payload = {
        title: `Term ${examForm.term} Exam: Year ${examForm.year_level} ${subjectLabel}`,
        subject: examForm.subject, year_level: examForm.year_level, topic: examForm.topics_covered,
        assessment_type: 'termly_exam', difficulty: 'mixed', total_marks: examForm.total_marks,
        questions: allQuestions, answer_key: generatedExam.answer_key || [],
        time_limit: examForm.duration_minutes, content: JSON.stringify(generatedExam)
      };
      const response = await resilientFetch(`${API_URL}/api/ai-lessons/save-assessment?teacher_id=${currentUser.id}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload)
      });
      if (!response.ok) throw new Error('Failed to save');
      toast.success('Termly exam saved to library!');
      loadSavedAssessments();
    } catch (error) {
      toast.error('Failed to save exam');
    }
  };

  // ========== Delete handlers ==========
  const deleteLesson = async (lessonId) => {
    if (!currentUser?.id) return;
    try {
      const response = await resilientFetch(`${API_URL}/api/ai-lessons/lesson/${lessonId}?teacher_id=${currentUser.id}`, { method: 'DELETE' });
      if (response.ok) { toast.success('Lesson deleted'); loadSavedLessons(); }
    } catch (error) {
      console.error('Error deleting lesson:', error);
      toast.error('Failed to delete lesson');
    }
  };

  const deleteAssessment = async (assessmentId, force = false) => {
    if (!currentUser?.id) return;
    try {
      const url = `${API_URL}/api/ai-lessons/assessment/${assessmentId}?teacher_id=${currentUser.id}${force ? '&force=true' : ''}`;
      const response = await resilientFetch(url, { method: 'DELETE' });
      if (response.ok) { toast.success('Assessment deleted'); loadSavedAssessments(); }
      else if (response.status === 409) {
        const data = await response.json();
        if (window.confirm(`WARNING: Students have submitted work!\n\n${data.detail}\n\nAre you SURE?`)) {
          deleteAssessment(assessmentId, true);
        }
      }
    } catch (error) {
      console.error('Error deleting assessment:', error);
      toast.error('Failed to delete assessment');
    }
  };

  // ========== Print handlers ==========
  const printScheme = () => {
    if (!generatedScheme) return;
    const subjectLabel = getSubjectOptions().find(s => s.value === schemeForm.subject)?.label || schemeForm.subject;
    const colors = ['#3b82f6','#10b981','#f59e0b','#8b5cf6','#f43f5e','#14b8a6','#6366f1','#f97316','#06b6d4','#ec4899'];
    const bgColors = ['#eff6ff','#ecfdf5','#fffbeb','#f5f3ff','#fff1f2','#f0fdfa','#eef2ff','#fff7ed','#ecfeff','#fdf2f8'];
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`<!DOCTYPE html><html><head><title>Scheme of Work - Year ${schemeForm.year_level} ${subjectLabel} Term ${schemeForm.term}</title>
    <style>body{font-family:'Segoe UI',Arial,sans-serif;padding:20px;max-width:1100px;margin:0 auto;color:#1e293b}.header{background:linear-gradient(135deg,#1e293b 0%,#334155 100%);color:white;padding:24px 28px;border-radius:12px;margin-bottom:24px}.header h1{margin:0 0 8px 0;font-size:22pt}.header .meta{font-size:11pt;opacity:.85;display:flex;gap:16px;flex-wrap:wrap}.header .meta span{background:rgba(255,255,255,.15);padding:4px 12px;border-radius:20px}table{width:100%;border-collapse:separate;border-spacing:0;margin-top:4px;font-size:10pt;border-radius:8px;overflow:hidden;box-shadow:0 1px 3px rgba(0,0,0,.1)}thead th{background:#1e293b;color:white;padding:12px 10px;text-align:left;font-weight:600}thead th:first-child{text-align:center;width:55px}td{padding:10px;border-bottom:1px solid #e2e8f0;vertical-align:top}.week-num{display:inline-flex;align-items:center;justify-content:center;width:32px;height:32px;border-radius:50%;color:white;font-weight:700;font-size:12px}.topic{font-weight:600;color:#1e293b}ul{margin:2px 0;padding-left:16px}li{margin:3px 0;color:#475569}.footer{margin-top:20px;text-align:center;color:#94a3b8;font-size:9pt;border-top:1px solid #e2e8f0;padding-top:12px}@media print{body{padding:10px}.no-print{display:none!important}.header,thead th,.week-num,tr{-webkit-print-color-adjust:exact;print-color-adjust:exact}table{box-shadow:none}}</style></head><body>
    <div class="no-print" style="margin-bottom:16px"><button onclick="window.print()" style="padding:10px 24px;background:#1e293b;color:white;border:none;border-radius:8px;cursor:pointer;font-size:14px;margin-right:8px">Print</button><button onclick="window.close()" style="padding:10px 24px;background:#e2e8f0;border:none;border-radius:8px;cursor:pointer;font-size:14px">Close</button></div>
    <div class="header"><h1>Scheme of Work</h1><div class="meta"><span><strong>Subject:</strong> ${subjectLabel}</span><span><strong>Year Level:</strong> ${schemeForm.year_level}</span><span><strong>Term:</strong> ${schemeForm.term}</span><span><strong>Duration:</strong> ${schemeForm.num_weeks} Weeks</span></div></div>
    <table><thead><tr><th>Week</th><th style="width:17%">Topic</th><th style="width:24%">Objectives</th><th style="width:24%">Activities</th><th style="width:13%">Resources</th><th style="width:13%">Assessment</th></tr></thead><tbody>
    ${generatedScheme.map((w, i) => `<tr style="background:${bgColors[i % bgColors.length]};border-left:4px solid ${colors[i % colors.length]};"><td style="text-align:center;"><span class="week-num" style="background:${colors[i % colors.length]};">${w.week}</span></td><td class="topic">${w.topic}</td><td><ul>${(w.learning_objectives||[]).map(o=>`<li>${o}</li>`).join('')}</ul></td><td><ul>${(w.activities||[]).map(a=>`<li>${a}</li>`).join('')}</ul></td><td style="color:#64748b;font-size:9.5pt;">${w.resources||''}</td><td style="color:#64748b;font-size:9.5pt;">${w.assessment||''}</td></tr>`).join('')}
    </tbody></table><div class="footer">Penama Education Portal | Generated ${new Date().toLocaleDateString()}</div></body></html>`);
    printWindow.document.close();
  };

  const printTermlyExam = () => {
    if (!generatedExam) return;
    const subjectLabel = getSubjectOptions().find(s => s.value === examForm.subject)?.label || examForm.subject;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`<!DOCTYPE html><html><head><title>Term ${examForm.term} Exam - ${subjectLabel}</title>
    <style>body{font-family:'Segoe UI',Arial,sans-serif;max-width:800px;margin:0 auto;padding:30px;color:#1a1a1a;line-height:1.5}.header{text-align:center;border:2px solid #1e3a5f;padding:20px;margin-bottom:24px}.header h1{color:#1e3a5f;margin:0;font-size:20px}.header h2{color:#374151;margin:6px 0;font-size:16px}.info-row{display:flex;justify-content:space-between;margin-top:12px;font-size:13px;color:#4b5563}.student-info{border:1px solid #d1d5db;padding:10px 16px;margin-bottom:20px}.student-info span{margin-right:40px}.instructions{background:#f3f4f6;padding:12px 16px;border-radius:6px;margin-bottom:20px;font-size:13px}.instructions h3{margin:0 0 6px 0;font-size:14px}.instructions ol{margin:4px 0;padding-left:20px}.section{margin-bottom:24px;page-break-inside:avoid}.section-header{background:#1e3a5f;color:white;padding:8px 14px;font-weight:bold;font-size:14px}.question{padding:8px 0;border-bottom:1px solid #e5e7eb}.question-num{font-weight:bold}.marks{float:right;color:#6b7280;font-size:12px}.options{padding-left:24px;margin:4px 0}.options div{margin:2px 0}.answer-lines{border-bottom:1px dotted #9ca3af;height:24px;margin:4px 0}.total{text-align:right;font-weight:bold;font-size:16px;margin-top:20px;padding-top:10px;border-top:2px solid #1e3a5f}.footer{text-align:center;color:#6b7280;font-size:11px;margin-top:30px}@media print{.section{page-break-inside:avoid}}</style></head><body>
    <div class="header"><h1>PENAMA PROVINCE</h1><h2>Year ${examForm.year_level} ${subjectLabel}</h2><h2>Term ${examForm.term} Examination ${new Date().getFullYear()}</h2><div class="info-row"><span>Duration: ${examForm.duration_minutes} minutes</span><span>Total Marks: ${examForm.total_marks}</span></div></div>
    <div class="student-info"><span>Name: ___________________________</span><span>Class: __________</span><span>Date: __________</span></div>
    <div class="instructions"><h3>Instructions</h3><ol><li>Write your name and class clearly above.</li><li>Answer ALL questions.</li><li>Read each question carefully before answering.</li><li>The marks for each question are shown in brackets.</li></ol></div>
    ${(generatedExam.sections||[]).map(section=>`<div class="section"><div class="section-header">${section.title} ${section.marks?`(${section.marks} marks)`:``}</div>${section.instructions?`<p style="font-size:13px;color:#4b5563;margin:6px 0;">${section.instructions}</p>`:``}${(section.questions||[]).map((q,qi)=>`<div class="question"><span class="question-num">${q.number||qi+1}.</span> ${q.text||q.question}<span class="marks">[${q.marks||1} mark${(q.marks||1)>1?'s':''}]</span>${q.options?`<div class="options">${q.options.map((o,oi)=>`<div>${String.fromCharCode(65+oi)}. ${o}</div>`).join('')}</div>`:`<div class="answer-lines"></div><div class="answer-lines"></div>`}</div>`).join('')}</div>`).join('')}
    <div class="total">Total: ______ / ${examForm.total_marks}</div>
    <div class="footer">Penama Education Portal | Generated ${new Date().toLocaleDateString()}</div></body></html>`);
    printWindow.document.close();
  };

  const printLesson = (lesson) => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`<!DOCTYPE html><html><head><title>${lesson.title}</title>
    <style>body{font-family:Arial,sans-serif;padding:20px;max-width:800px;margin:0 auto}h1{color:#333;border-bottom:2px solid #333;padding-bottom:10px}h2{color:#555;margin-top:20px}h3{color:#666}ul{margin-left:20px}.activity{background:#f5f5f5;padding:15px;margin:10px 0;border-radius:8px}.meta{color:#888;font-size:14px;margin-bottom:20px}@media print{body{padding:0}.no-print{display:none}}</style></head><body>
    <h1>${lesson.title}</h1>
    <div class="meta"><strong>Duration:</strong> ${lesson.duration || lessonForm.duration} minutes | <strong>Subject:</strong> ${lessonForm.subject.replace('_', ' ')} | <strong>Year Level:</strong> Year ${lessonForm.year_level}</div>
    <h2>Learning Objectives</h2><ul>${(lesson.objectives||[]).map(obj=>`<li>${obj}</li>`).join('')}</ul>
    <h2>Introduction</h2><p>${lesson.introduction}</p>
    <h2>Activities</h2>${(lesson.activities||[]).map(act=>`<div class="activity"><h3>${act.name} (${act.duration})</h3><p>${act.description}</p></div>`).join('')}
    <h2>Materials</h2><p>${lesson.materials}</p>
    <h2>Assessment</h2><p>${lesson.assessment}</p>
    <h2>Homework</h2><p>${lesson.homework}</p>
    <button class="no-print" onclick="window.print()" style="margin-top:20px;padding:10px 20px;">Print</button></body></html>`);
    printWindow.document.close();
  };

  // ========== Render ==========
  return (
    <div className="space-y-6" data-testid="ai-lesson-planner-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between gap-4 md:items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <Sparkles className="h-8 w-8 text-purple-600" />
            AI Teacher Hub
          </h1>
          <p className="text-muted-foreground mt-1">
            Generate lesson plans, study notes with AI diagrams, assessments & more
          </p>
        </div>
        <Badge variant="outline" className="w-fit bg-purple-50 text-purple-700 border-purple-200">
          <Sparkles className="h-3 w-3 mr-1" />
          Powered by Gemini 3 Flash
        </Badge>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-9">
          <TabsTrigger value="generate" className="flex items-center gap-2" data-testid="tab-generate">
            <Sparkles className="h-4 w-4" />
            <span className="hidden sm:inline">Lesson</span>
          </TabsTrigger>
          <TabsTrigger value="week" className="flex items-center gap-2" data-testid="tab-week">
            <Calendar className="h-4 w-4" />
            <span className="hidden sm:inline">Week Plan</span>
          </TabsTrigger>
          <TabsTrigger value="scheme" className="flex items-center gap-2" data-testid="tab-scheme">
            <LayoutList className="h-4 w-4" />
            <span className="hidden sm:inline">Scheme</span>
          </TabsTrigger>
          <TabsTrigger value="notes" className="flex items-center gap-2" data-testid="tab-notes">
            <FileText className="h-4 w-4" />
            <span className="hidden sm:inline">Notes</span>
          </TabsTrigger>
          <TabsTrigger value="exam" className="flex items-center gap-2" data-testid="tab-exam">
            <GraduationCap className="h-4 w-4" />
            <span className="hidden sm:inline">Exam</span>
          </TabsTrigger>
          <TabsTrigger value="assessment" className="flex items-center gap-2" data-testid="tab-assessment">
            <ClipboardList className="h-4 w-4" />
            <span className="hidden sm:inline">Assessment</span>
          </TabsTrigger>
          <TabsTrigger value="batch" className="flex items-center gap-2" data-testid="tab-batch">
            <Layers className="h-4 w-4" />
            <span className="hidden sm:inline">Batch</span>
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2" data-testid="tab-history">
            <History className="h-4 w-4" />
            <span className="hidden sm:inline">Library</span>
          </TabsTrigger>
          <TabsTrigger value="submissions" className="flex items-center gap-2" data-testid="tab-submissions">
            <Send className="h-4 w-4" />
            <span className="hidden sm:inline">My Submissions</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="generate" className="space-y-6">
          <GenerateLessonTab
            lessonForm={lessonForm} setLessonForm={setLessonForm}
            loading={loading} generateLesson={generateLesson}
            generatedLesson={generatedLesson} saveLesson={saveLesson}
            printLesson={printLesson} shareToAcademic={shareLessonToAcademic}
            sharingToAcademic={sharingToAcademic}
            getSubjectOptions={getSubjectOptions}
            getYearOptions={getYearOptions}
            shareLabel={shareLabel}
          />
        </TabsContent>

        <TabsContent value="week" className="space-y-6">
          <WeekPlanTab
            lessonForm={lessonForm} setLessonForm={setLessonForm}
            loading={loading} generateWeekPlan={generateWeekPlan}
            generatedWeek={generatedWeek} saveLesson={saveLesson}
            shareToAcademic={shareLessonToAcademic} sharingToAcademic={sharingToAcademic}
            getSubjectOptions={getSubjectOptions} getYearOptions={getYearOptions}
            shareLabel={shareLabel}
          />
        </TabsContent>

        <TabsContent value="scheme" className="space-y-6">
          <SchemeTab
            schemeForm={schemeForm} setSchemeForm={setSchemeForm}
            schemeLoading={schemeLoading} generateScheme={generateScheme}
            generatedScheme={generatedScheme} saveScheme={saveScheme}
            printScheme={printScheme} shareToAcademic={shareSchemeToAcademic}
            sharingToAcademic={sharingToAcademic}
            getSubjectOptions={getSubjectOptions}
            getYearOptions={getYearOptions}
            shareLabel={shareLabel}
          />
        </TabsContent>

        <TabsContent value="notes" className="space-y-6">
          <NotesTab
            currentUser={currentUser}
            getSubjectOptions={getSubjectOptions}
            getYearOptions={getYearOptions}
          />
        </TabsContent>

        <TabsContent value="exam" className="space-y-6">
          <ExamTab
            examForm={examForm} setExamForm={setExamForm}
            examLoading={examLoading} generateTermlyExam={generateTermlyExam}
            generatedExam={generatedExam} saveTermlyExam={saveTermlyExam}
            printTermlyExam={printTermlyExam} getSubjectOptions={getSubjectOptions}
            getYearOptions={getYearOptions}
          />
        </TabsContent>

        <TabsContent value="assessment" className="space-y-6">
          <AssessmentTab
            assessmentForm={assessmentForm} setAssessmentForm={setAssessmentForm}
            loading={loading} generateAssessment={generateAssessment}
            generatedAssessment={generatedAssessment} saveAssessment={saveAssessment}
            getSubjectOptions={getSubjectOptions} getYearOptions={getYearOptions}
          />
        </TabsContent>

        <TabsContent value="batch" className="space-y-6">
          <BatchGeneratorTab currentUser={currentUser} />
        </TabsContent>

        <TabsContent value="history" className="space-y-6">
          <HistoryTab
            savedLessons={savedLessons} savedAssessments={savedAssessments}
            loadingSaved={loadingSaved} selectedTag={selectedTag}
            setSelectedTag={setSelectedTag} allTags={allTags}
            editingTags={editingTags} setEditingTags={setEditingTags}
            newTagInput={newTagInput} setNewTagInput={setNewTagInput}
            updateItemTags={updateItemTags} sharedContentIds={sharedContentIds}
            shareNote={shareNote} shareAssessment={shareAssessment}
            unshareContent={unshareContent} deleteLesson={deleteLesson}
            deleteAssessment={deleteAssessment} viewingSubmissions={viewingSubmissions}
            setViewingSubmissions={setViewingSubmissions} submissionsData={submissionsData}
            setSubmissionsData={setSubmissionsData} loadingSubmissions={loadingSubmissions}
            viewSubmissions={viewSubmissions} gradeSubmission={gradeSubmission}
          />
        </TabsContent>

        {/* My Submissions Tab - Shows teacher's submitted schemes/lesson plans to Academic */}
        <TabsContent value="submissions" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Send className="h-5 w-5" />
                My Submissions to {submissionTarget}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loadingSubmissions ? (
                <div className="text-center py-8 text-gray-500">Loading submissions...</div>
              ) : teacherSubmissions.length === 0 ? (
                <div className="text-center py-8">
                  <Send className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                  <p className="text-gray-500">No submissions yet</p>
                  <p className="text-sm text-gray-400">Share your Schemes and Lesson Plans to {submissionTarget} for approval</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {teacherSubmissions.map((sub) => (
                    <div key={sub.id} className="p-4 border rounded-lg">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant="secondary" className="text-xs capitalize">
                              {sub.submission_type === 'scheme' ? 'Scheme of Work' : 'Lesson Plan'}
                            </Badge>
                            {sub.status === 'pending' && (
                              <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-300">
                                <Clock className="h-3 w-3 mr-1" /> Pending Review
                              </Badge>
                            )}
                            {sub.status === 'approved' && (
                              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">
                                <CheckCircle className="h-3 w-3 mr-1" /> Approved
                              </Badge>
                            )}
                            {sub.status === 'rejected' && (
                              <Badge variant="outline" className="bg-red-50 text-red-700 border-red-300">
                                <XCircle className="h-3 w-3 mr-1" /> Rejected
                              </Badge>
                            )}
                          </div>
                          <h4 className="font-medium">{sub.title}</h4>
                          <p className="text-sm text-gray-500">
                            {sub.subject} • Year {sub.year_level} • Term {sub.term}
                          </p>
                        </div>
                        <div className="text-right text-xs text-gray-400">
                          <p>Submitted: {new Date(sub.submitted_at).toLocaleDateString()}</p>
                          {sub.reviewed_at && (
                            <p>Reviewed: {new Date(sub.reviewed_at).toLocaleDateString()}</p>
                          )}
                        </div>
                      </div>
                      {sub.reviewer_comment && (
                        <div className="mt-3 p-2 bg-gray-50 rounded text-sm">
                          <strong>Comment:</strong> {sub.reviewer_comment}
                        </div>
                      )}
                      {sub.status === 'approved' && (
                        <SubmissionContentViewer submissionId={sub.id} type={sub.submission_type} />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Share Assessment Dialog */}
      {shareDialog && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center" data-testid="share-dialog">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Share Assessment with Students</h3>
            
            <div className="space-y-4">
              {/* Lockdown toggle */}
              <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer">
                <div>
                  <p className="font-medium text-sm text-gray-900">Exam Lockdown Mode</p>
                  <p className="text-xs text-gray-500">
                    {shareLockdown 
                      ? 'Students portal will be locked during the test — auto-detected as test/quiz/exam' 
                      : 'Open mode — students can navigate freely (worksheet/practice)'}
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={shareLockdown}
                  onChange={(e) => setShareLockdown(e.target.checked)}
                  className="w-5 h-5 accent-emerald-600"
                  data-testid="lockdown-toggle"
                />
              </label>

              {/* Tab switch warnings */}
              {shareLockdown && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="font-medium text-sm text-gray-900 mb-2">Tab Switch Warnings</p>
                  <p className="text-xs text-gray-500 mb-2">Auto-submit after this many tab switches (0 = log only)</p>
                  <select
                    value={shareMaxTabSwitches}
                    onChange={(e) => setShareMaxTabSwitches(Number(e.target.value))}
                    className="w-full p-2 border rounded-lg text-sm"
                    data-testid="max-tab-switches"
                  >
                    <option value={0}>Log only (no auto-submit)</option>
                    <option value={1}>1 warning then auto-submit</option>
                    <option value={2}>2 warnings then auto-submit</option>
                    <option value={3}>3 warnings then auto-submit</option>
                  </select>
                </div>
              )}

              {/* Info about always-on features */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-xs font-medium text-amber-800 mb-1">Always Active (all assessments):</p>
                <ul className="text-xs text-amber-700 space-y-0.5">
                  <li>- Paste activity is detected and logged</li>
                  <li>- Typing patterns are tracked</li>
                  <li>- AI detection scan on text answers</li>
                </ul>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShareDialog(null)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
                data-testid="cancel-share-btn"
              >
                Cancel
              </button>
              <button
                onClick={confirmShareAssessment}
                className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm hover:bg-emerald-700"
                data-testid="confirm-share-btn"
              >
                Share with Students
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
